import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { Session } from "@supabase/supabase-js";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import { useSession } from "./SessionProvider";

/**
 * Whether this session still owes a second factor, and what to say if it does.
 *
 * architecture.md section 4: a platform admin needs a verified TOTP factor and an `aal2` session for
 * every admin route and every admin RPC. `public.is_platform_admin()` enforces it in the database,
 * and until now nothing in the application answered a challenge, so an admin could sign in
 * successfully and then be refused by every admin gateway with no screen that explained why.
 *
 * This file decides which of four situations a signed-in session is in. It decides nothing about
 * access: the database re-derives that from the same token on every call, and a browser that lied to
 * itself here would get exactly as far as it does today, which is nowhere.
 *
 * Enrollment is not here, and that is now a division of labour rather than a gap. It lives in
 * `app/auth/mfaEnrollment.ts` with the rest of account settings, where `startEnrollment` and
 * `confirmEnrollment` create and prove a factor. This file decides what an existing factor means for
 * the session in front of it, and answers the challenge. The two meet at `listFactors`, which both
 * read and neither caches on the other's behalf.
 */

export type MfaGate =
  /** Nothing to answer: no verified factor, or one already answered on this session. */
  | { kind: "satisfied"; adminRequired: boolean }
  /** A verified factor exists and this session is still `aal1`. */
  | { kind: "challenge-required"; adminRequired: boolean; factorId: string }
  /**
   * The allow-list requires a second factor and there is no factor to answer with.
   *
   * Reachable by the person who holds the allow-list row, by deleting their own factor. Without a
   * name for it, this state is indistinguishable from an ordinary client session right up until
   * every admin gateway refuses, which is why `public.current_mfa_requirement` exists.
   */
  | { kind: "enrollment-missing"; adminRequired: true };

/**
 * The assurance level of the session, read from the token it is carrying.
 *
 * Read from the JWT rather than from `getAuthenticatorAssuranceLevel`, for the same reason
 * `SessionProvider` reads `session_id` that way: the claim is the thing the server will act on, and
 * the alternative computes `nextLevel` from `user.factors`, which is a copy taken when the session
 * was issued and does not change when a factor does.
 */
export function readAssuranceLevel(session: Session | null): string | null {
  if (!session?.access_token) {
    return null;
  }

  try {
    const [, payload] = session.access_token.split(".");
    const claims = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))) as {
      aal?: unknown;
    };
    return typeof claims.aal === "string" ? claims.aal : null;
  } catch {
    return null;
  }
}

/**
 * Asks the server what it knows, then decides.
 *
 * Both calls go out together. The factor list is needed in every case and the requirement flag only
 * in one, but sequencing them would put a second round trip in front of every client's sign-in to
 * answer a question that is `false` for all of them. One round trip of latency, once per session.
 */
export async function evaluateMfaGate(session: Session | null): Promise<MfaGate> {
  const supabase = getSupabaseBrowserClient();

  const [factors, requirement] = await Promise.all([
    supabase.auth.mfa.listFactors(),
    supabase.rpc("current_mfa_requirement")
  ]);

  if (factors.error) {
    throw factors.error;
  }
  if (requirement.error) {
    throw requirement.error;
  }

  const adminRequired = requirement.data === true;
  // `listFactors` already filters `totp` down to the verified ones. An enrolment that was started
  // and never confirmed is not something to challenge against.
  const verified = factors.data.totp[0] ?? null;

  if (verified) {
    return readAssuranceLevel(session) === "aal2"
      ? { kind: "satisfied", adminRequired }
      : { kind: "challenge-required", adminRequired, factorId: verified.id };
  }

  return adminRequired
    ? { kind: "enrollment-missing", adminRequired: true }
    : { kind: "satisfied", adminRequired: false };
}

/**
 * The gate as a query, keyed on the token.
 *
 * Keyed on the token and not on the user, because answering the challenge is what changes the
 * answer: `mfa.verify` issues a new access token at `aal2`, `SessionProvider` picks it up, and the
 * key changes with it. Nothing has to remember to invalidate.
 *
 * **A satisfied answer is carried across the new key, and nothing else is.** A new key has no data,
 * so the query was pending for one round trip every time the token changed, and `RequireSession`
 * renders a loading block for a pending gate in place of everything under it. Every step-up that
 * answers a TOTP code calls `mfa.verify` and so mints a new token, and the automatic refresh mints
 * one every hour. The whole portal unmounted each time. Found in the reviewer F walkthrough on
 * 2026-09-13: Petrovic opened a client's one-time secret on /admin/clients, the Worker consumed it
 * and answered 200, and the value was handed to a component that no longer existed. The secret was
 * spent and never shown. A half-written message or request is lost the same way.
 *
 * Only `satisfied` is carried, because it is the only answer a new token for the same identity cannot
 * make weaker: a verify or a refresh keeps or raises `aal`, and a change of identity goes through
 * `SIGNED_IN`, `USER_UPDATED` or `SIGNED_OUT`, which clear the query cache in `SessionProvider`
 * before a previous answer could be offered. A carried `challenge-required` would be wrong the other
 * way: the verification screen navigates on as soon as the code is accepted, and the guard would send
 * the person straight back to answer a challenge they had just answered. Nothing here grants access
 * either way; the database reads the token on every call.
 */
export function useMfaGate(): UseQueryResult<MfaGate> {
  const { session, status } = useSession();

  return useQuery({
    queryKey: ["mfa-gate", session?.access_token ?? "none"],
    enabled: status === "signed-in",
    // The answer is a property of this token and cannot change while the token is the same one.
    staleTime: Infinity,
    placeholderData: (previous) => (previous?.kind === "satisfied" ? previous : undefined),
    queryFn: () => evaluateMfaGate(session)
  });
}
